import clsx from "clsx";
import { createPluggable } from "pluggable-react";
import type React from "react";
import type { InputProps } from "../../../types/FormTypes";
import styles from "./Radio.module.scss";
import { useRadioGroup } from "./RadioGroup";

export interface RadioProps<TValue extends string = string>
  extends Omit<
      React.InputHTMLAttributes<HTMLInputElement>,
      "onBlur" | "onChange" | "onFocus" | "size" | "type" | "value"
    >,
    InputProps<TValue> {
  value: TValue;
  size?: "sm" | "md";
}

const DefaultRadio = <TValue extends string = string>({
  className,
  value,
  name,
  checked,
  onValueChange,
  onChange,
  onBlur,
  onFocus,
  disabled,
  required,
  readOnly,
  error,
  size = "md",
  ...props
}: RadioProps<TValue>) => {
  const group = useRadioGroup();
  const resolvedReadOnly = readOnly ?? group?.readOnly ?? false;
  const resolvedValue = value as string;

  return (
    <input
      {...props}
      className={clsx(styles.radio, className)}
      type="radio"
      name={name ?? group?.name}
      value={value}
      checked={checked ?? (group ? group.value === resolvedValue : undefined)}
      disabled={disabled ?? group?.disabled}
      required={required ?? group?.required}
      aria-invalid={(error ?? group?.error) || undefined}
      aria-readonly={resolvedReadOnly || undefined}
      data-deci-radio="true"
      data-size={size}
      onBlur={(event) => {
        onBlur?.(event);
        group?.onBlur?.(event);
      }}
      onFocus={(event) => {
        onFocus?.(event);
        group?.onFocus?.(event);
      }}
      onChange={(event) => {
        if (resolvedReadOnly) {
          event.preventDefault();
          return;
        }
        onValueChange?.(value);
        group?.onValueChange?.(resolvedValue);
        onChange?.(event);
        group?.onChange?.(event);
      }}
    />
  );
};

const Radio = createPluggable<RadioProps<any>>("deci.radio", DefaultRadio);

export default Radio;
